import React, { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";
import DashboardLayout from "./components/DashboardLayout";
import DistributeurCard from "./components/DistributeurCard";
import VenteCard from "./components/Ventecard";
import RefillCard from "./components/Refillcard";
import DistributeurDetailModal from "./components/DistributeurDetailModal";
import FeedbackPage from "./components/FeedbackPage";

function App() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [distributeurs, setDistributeurs] = useState([]);
  const [sales, setSales] = useState([]);
  const [refills, setRefills] = useState([]);
  const [selectedDistributeur, setSelectedDistributeur] = useState(null);

  // Auth
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user || null);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setError(error.message);
    setLoading(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  // Load data from Supabase + real-time subscriptions
  useEffect(() => {
    const load = async () => {
      const [{ data: dists }, { data: dks }, { data: ventes }, { data: recharges }] = await Promise.all([
        supabase.from("distributeurs").select("*"),
        supabase.from("drinks").select("*"),
        supabase.from("sales").select("*").order("timestamp", { ascending: false }).limit(10),
        supabase.from("refills").select("*").order("timestamp", { ascending: false }).limit(10),
      ]);

      const grouped = (dks || []).reduce((acc, d) => {
        (acc[d.distributeur_id] ||= []).push(d);
        return acc;
      }, {});

      setDistributeurs((dists || []).map((d) => ({ ...d, drinks: grouped[d.id] || [] })));
      setSales(ventes || []);
      setRefills(recharges || []);
    };

    load();

    // Real-time: distributeurs
    const sub = supabase
      .channel('app-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'distributeurs' },
        () => supabase.from("distributeurs").select("*").then(({ data }) => {
          setDistributeurs((prev) => (data || []).map((d) => ({
            ...d,
            drinks: prev.find((p) => p.id === d.id)?.drinks || [],
          })));
        })
      )
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'sales' },
        ({ new: row }) => setSales((prev) => [row, ...prev.slice(0, 9)])
      )
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'refills' },
        ({ new: row }) => setRefills((prev) => [row, ...prev.slice(0, 9)])
      )
      .subscribe();

    return () => supabase.removeChannel(sub);
  }, []);

  const handleDeleteDistributeur = async (id) => {
    const confirm = window.confirm(`Supprimer le distributeur ${id} ?`);
    if (!confirm) return;
    const { error } = await supabase.from("distributeurs").delete().eq("id", id);
    if (!error) setDistributeurs((prev) => prev.filter((d) => d.id !== id));
  };

  const handleDisable = async (id) => {
    const confirm = window.confirm(`Désactiver le distributeur ${id} ?`);
    if (!confirm) return;

    // Send disable command via Supabase
    const { error } = await supabase.from("commands").insert({
      distributeur_id: id,
      command_type: "disable",
      payload: {}
    });

    if (error) {
      alert(`Erreur: ${error.message}`);
    } else {
      alert(`Commande de désactivation envoyée au distributeur ${id}`);
    }
  };

  const handleRestart = async (id) => {
    const confirm = window.confirm(`Redémarrer le distributeur ${id} ?`);
    if (!confirm) return;

    // Send restart command via Supabase
    const { error } = await supabase.from("commands").insert({
      distributeur_id: id,
      command_type: "restart",
      payload: {}
    });

    if (error) {
      alert(`Erreur: ${error.message}`);
    } else {
      alert(`Commande de redémarrage envoyée au distributeur ${id}`);
    }
  };

  if (loading) {
    return <div style={{ padding: "2rem", textAlign: "center" }}>Chargement...</div>;
  }

  if (!user) {
    return (
      <div style={{ padding: "2rem", fontFamily: "sans-serif" }}>
        <div style={{ maxWidth: 400, margin: "auto" }}>
          <h2>KIWO Admin Login</h2>
          <form onSubmit={handleLogin}>
            <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
              style={{ padding: "0.5rem", width: "100%", marginBottom: "1rem" }} required />
            <input type="password" placeholder="Mot de passe" value={password} onChange={(e) => setPassword(e.target.value)}
              style={{ padding: "0.5rem", width: "100%", marginBottom: "1rem" }} required />
            {error && <div style={{ color: "red", marginBottom: "1rem", fontSize: "0.9rem" }}>{error}</div>}
            <button type="submit" disabled={loading} style={{ padding: "0.5rem 1rem", width: "100%" }}>
              {loading ? "Connexion..." : "Connexion"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "2rem", fontFamily: "sans-serif", backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <DashboardLayout onLogout={handleLogout} />

      <h2 style={{ color: "#1e293b", marginBottom: "1.5rem", fontSize: "1.2rem", fontWeight: 700 }}>Distributeurs connectés</h2>
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '2rem',
        overflowX: 'auto',
        paddingTop: '0.5rem',
        paddingBottom: '0.5rem'
      }}>
        {distributeurs.length === 0 ? (
          <div style={{
            background: '#fff',
            borderRadius: 16,
            padding: '2rem',
            textAlign: 'center',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            width: '100%'
          }}>
            <p style={{ color: "#94a3b8", fontSize: "0.9rem", margin: 0 }}>Aucun distributeur connecté</p>
          </div>
        ) : (
          distributeurs.map((d) => (
            <DistributeurCard key={d.id} d={d} onDelete={handleDeleteDistributeur} onClick={setSelectedDistributeur} />
          ))
        )}
      </div>

      <FeedbackPage distributeurs={distributeurs} />

      <VenteCard sales={sales} distributeurs={distributeurs} />
      <RefillCard refills={refills} distributeurs={distributeurs} />

      {selectedDistributeur && (
        <DistributeurDetailModal
          distributeur={selectedDistributeur}
          onClose={() => setSelectedDistributeur(null)}
          onRestart={handleRestart}
          onDisable={handleDisable}
          logs={[]}
        />
      )}
    </div>
  );
}

export default App;
